import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {API_END_POINT} from '@shared/utils/api-end-points';
import {EnvService} from '@services/env.service';
import {ISignalRConnection, SignalR, SignalRConfiguration, SignalRConnection} from 'ng2-signalr';
import {
  IImportValidationCellEdit,
  IImportValidationCellUpdateResult,
  IImportValidationCleanupPreview,
  IImportValidationCleanupRequest,
  IImportValidationCleanupResult,
  IImportValidationHubEvent,
  IImportValidationProgress,
  IImportValidationRow,
  IImportValidationSession,
  IImportValidationStage,
  ImportValidationPhaseCode
} from '@shared/models/iimport-validation';

@Injectable({providedIn: 'root'})
export class ImportValidationService {
  private connection: SignalRConnection;
  stageProgress$ = new Subject<IImportValidationProgress>();
  private disconnect$ = new Subject<void>();
  static readonly supportTokenHeader = 'X-Import-Support-Token';
  private static readonly supportTokenPrefix = 'viu.supportToken.';
  private static readonly supportFlagPrefix = 'viu.supportAuthorized.';

  constructor(
    private http: HttpClient,
    private signalR: SignalR,
    private envService: EnvService
  ) {}

  authorizeSupport(appId: string): Observable<{ authorized: boolean; supportToken?: string; message?: string }> {
    return this.http.post<{ authorized: boolean; supportToken?: string; message?: string }>(
      API_END_POINT.payroll.importValidation + 'AuthorizeSupport', { appId });
  }

  revokeSupportAuthorization(): Observable<{ authorized: boolean }> {
    return this.http.post<{ authorized: boolean }>(
      API_END_POINT.payroll.importValidation + 'RevokeSupportAuthorization', {});
  }

  static supportTokenStorageKey(companyId: string, userId: string): string {
    return `${ImportValidationService.supportTokenPrefix}${companyId}.${userId}`;
  }

  static getSupportToken(companyId: string, userId: string): string | null {
    if (!companyId || !userId) { return null; }
    return sessionStorage.getItem(ImportValidationService.supportTokenStorageKey(companyId, userId));
  }

  static saveSupportToken(companyId: string, userId: string, token: string): void {
    if (!companyId || !userId || !token) { return; }
    sessionStorage.setItem(ImportValidationService.supportTokenStorageKey(companyId, userId), token);
  }

  static clearSupportToken(companyId?: string, userId?: string): void {
    if (companyId && userId) {
      sessionStorage.removeItem(ImportValidationService.supportTokenStorageKey(companyId, userId));
      return;
    }
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(ImportValidationService.supportTokenPrefix)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  /** Clears Workbench support unlock flags (login session). Call on logout. */
  static clearSupportAuthorizedLocalStorage(): void {
    ImportValidationService.clearSupportToken();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(ImportValidationService.supportFlagPrefix)
        || key.startsWith(ImportValidationService.supportTokenPrefix))) {
        localStorage.removeItem(key);
      }
    }
  }

  createSession(): Observable<{ sessionId: string }> {
    return this.http.post<{ sessionId: string }>(API_END_POINT.payroll.importValidation + 'CreateSession', {});
  }

  getSession(sessionId: string): Observable<IImportValidationSession> {
    return this.http.get<IImportValidationSession>(API_END_POINT.payroll.importValidation + 'GetSession?sessionId=' + sessionId);
  }

  // Deletes the session (every stage/row/error cascades away server-side) and returns a brand new,
  // empty one for the same company/user -- a full "start over" for the whole workbench.
  resetWorkbench(sessionId: string): Observable<{ sessionId: string }> {
    return this.http.post<{ sessionId: string }>(
      API_END_POINT.payroll.importValidation + 'ResetWorkbench?sessionId=' + sessionId, {});
  }

  uploadStage(sessionId: string, phaseCode: ImportValidationPhaseCode, file: File, stageConfig?: any): Observable<{ stageId: string }> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (stageConfig) {
      form.append('stageConfig', JSON.stringify(stageConfig));
    }
    return this.http.post<{ stageId: string }>(
      API_END_POINT.payroll.importValidation + `UploadStage?sessionId=${sessionId}&phaseCode=${phaseCode}`, form);
  }

  getRows(stageId: string, pageNumber: number, pageSize: number,
          errorsOnly = false, warningsOnly = false, searchText: string = null): Observable<IImportValidationRow[]> {
    let url = `${API_END_POINT.payroll.importValidation}GetRows?stageId=${stageId}&pageNumber=${pageNumber}&pageSize=${pageSize}` +
      `&errorsOnly=${errorsOnly}&warningsOnly=${warningsOnly}`;
    if (searchText) { url += `&searchText=${encodeURIComponent(searchText)}`; }
    return this.http.get<IImportValidationRow[]>(url);
  }

  updateCell(stageId: string, edit: IImportValidationCellEdit): Observable<IImportValidationCellUpdateResult> {
    return this.http.patch<IImportValidationCellUpdateResult>(
      API_END_POINT.payroll.importValidation + 'UpdateCell?stageId=' + stageId, edit);
  }

  deleteRow(stageId: string, rowId: number): Observable<IImportValidationCellUpdateResult> {
    return this.http.delete<IImportValidationCellUpdateResult>(
      API_END_POINT.payroll.importValidation + `DeleteRow?stageId=${stageId}&rowId=${rowId}`);
  }

  getStage(stageId: string): Observable<IImportValidationStage> {
    return this.http.get<IImportValidationStage>(API_END_POINT.payroll.importValidation + 'GetStage?stageId=' + stageId);
  }

  // Downloads go through HttpClient (not window.open()) so the JWT auth interceptor attaches correctly --
  // this controller requires bearer auth, and window.open() bypasses HttpClient's pipeline entirely.
  // Matches the download pattern already used elsewhere in this app (dynamic-report.component.ts's
  // downloadCsvResponse): GET via HttpClient with {observe:'response', responseType:'blob'}.
  // errorReport=true returns the annotated workbook (red invalid cells + Error Summary sheet).
  downloadStage(stageId: string, errorReport = false): Observable<HttpResponse<Blob>> {
    const qs = errorReport ? `&errorReport=true` : '';
    return this.http.get(API_END_POINT.payroll.importValidation + 'DownloadStage?stageId=' + stageId + qs, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  previewColumnCleanup(stageId: string, request: IImportValidationCleanupRequest): Observable<IImportValidationCleanupPreview> {
    return this.http.post<IImportValidationCleanupPreview>(
      API_END_POINT.payroll.importValidation + 'PreviewColumnCleanup?stageId=' + stageId, request);
  }

  applyColumnCleanup(stageId: string, request: IImportValidationCleanupRequest): Observable<IImportValidationCleanupResult> {
    return this.http.post<IImportValidationCleanupResult>(
      API_END_POINT.payroll.importValidation + 'ApplyColumnCleanup?stageId=' + stageId, request);
  }

  // Creates/updates MasterPaycard or PaycardHistory stage config for already-imported readiness
  // validation (FixImportedData dry-run). Does not mutate payroll data.
  prepareReadinessStage(sessionId: string, phaseCode: ImportValidationPhaseCode, stageConfig: any): Observable<{ stageId: string }> {
    return this.http.post<{ stageId: string }>(
      API_END_POINT.payroll.importValidation +
      `PrepareReadinessStage?sessionId=${sessionId}&phaseCode=${phaseCode}`, stageConfig);
  }

  bypassPrecedingStages(sessionId: string, targetPhaseCode: ImportValidationPhaseCode): Observable<{ bypassedPhaseCodes: ImportValidationPhaseCode[] }> {
    return this.http.post<{ bypassedPhaseCodes: ImportValidationPhaseCode[] }>(
      API_END_POINT.payroll.importValidation +
      `BypassPrecedingStages?sessionId=${sessionId}&targetPhaseCode=${targetPhaseCode}`, {});
  }

  // Reuses the existing salaryProcessHub connection AND event name exactly as-is: the server pushes
  // progress via SalaryProcessCompleteHandler.Subscribe, which fires the hub's 'processCompleted'
  // callback (there is no separate event for this feature). Every subscriber on this connection receives
  // every 'processCompleted' event pushed to it, so this filters on sessionId (== our stageId, see
  // IImportValidationHubEvent) to only react to the stage this call started, in case a payroll run is
  // *also* using the same open connection concurrently.
  async connectAndValidate(stageId: string): Promise<void> {
    if (!this.connection?.id) {
      await this.connect();
    }
    this.connection.listenFor('processCompleted').pipe(takeUntil(this.disconnect$))
      .subscribe((res: IImportValidationHubEvent) => {
        if (res.sessionId !== stageId) { return; }
        this.stageProgress$.next(this.toProgress(stageId, res));
      });

    // Server call that enqueues the Hangfire job, passing this connection's id so progress is pushed
    // only to this one browser tab (never broadcast).
    await this.http.post(API_END_POINT.payroll.importValidation +
      `ValidateStage?stageId=${stageId}&connectionId=${this.connection.id}`, {}).toPromise();
  }

  private toProgress(stageId: string, res: IImportValidationHubEvent): IImportValidationProgress {
    const text = res.message?.g ?? '';
    const percentage = res.percentage ?? 0;
    const completedPrefix = 'stageValidationCompleted|';
    if (text.startsWith(completedPrefix)) {
      // { stage, stalePhaseCodes } -- stalePhaseCodes lists any downstream stages this run
      // cascade-invalidated (see ImportValidationRepository.ValidateStageAsync), so the workbench can
      // nudge the user to revalidate those too, the same as it already does after an inline edit.
      const payload = JSON.parse(text.substring(completedPrefix.length));
      return {stageId, message: 'stageValidationCompleted', percentage, completedStage: payload.stage, stalePhaseCodes: payload.stalePhaseCodes};
    }
    if (text === 'stageValidationFailed') {
      return {stageId, message: text, percentage, failed: true};
    }
    return {stageId, message: text, percentage};
  }

  private async connect(): Promise<ISignalRConnection | void> {
    const c = new SignalRConfiguration();
    c.hubName = 'salaryProcessHub';
    c.url = this.envService.signalRHubUrl + '/salary';
    this.connection = this.signalR.createConnection(c);
    return this.connection.start();
  }

  disconnect() {
    this.disconnect$.next();
    this.connection?.stop();
  }
}
